// v2 Slider, Template «Strecke» (templates/areas/v2/slider/templates/roadtrip.html.twig)
// Verteilt die Halte gleichmässig über die Strasse, koppelt sie an den Swiper (index.js, .js-slider)
// und lässt das Auto zum aktiven Halt fahren. Unabhängig von den anderen Slider-Templates.
// sichtbarer Ausschnitt der Figma-Grafik (1100×320, oben 60 Einheiten Leerraum abgeschnitten)
const VIEWBOX = { y: 60, w: 1100, h: 260 };
// erster und letzter Halt in Figma (x-Position auf der Strasse)
const FIRST_X = 166;
const LAST_X = 1059.6;
// das Auto hält kurz vor dem Pin (wie in Figma)
const CAR_OFFSET = 26;

class RoadTrip {
  constructor() {
    this.containers = document.querySelectorAll('.js-roadtrip');
    this.reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  init() {
    if (this.containers.length === 0) {
      return;
    }

    this.containers.forEach((container) => this.setup(container));

    // index.js baut Slider bei Tab-Wechseln neu auf: dann neu koppeln
    document.addEventListener('tab-changed', () => {
      setTimeout(() => this.containers.forEach((container) => this.attachSwiper(container, 0)), 50);
    });
  }

  setup(container) {
    const road = container.querySelector('.roadtrip-road');
    const items = Array.prototype.slice.call(container.querySelectorAll('.roadtrip-stop-item'));
    const stops = Array.prototype.slice.call(container.querySelectorAll('.roadtrip-stop'));

    if (!road || stops.length === 0) {
      return;
    }

    const state = {
      road,
      done: container.querySelector('.roadtrip-done'),
      car: container.querySelector('.roadtrip-car'),
      stops,
      total: road.getTotalLength(),
      stopLength: [],
      carAt: 0,
      anim: null,
      started: false,
      active: 0,
      swiper: null
    };

    container.roadtrip = state;

    // «1 / 6» für Screenreader (die Anzahl kennt erst das Frontend)
    const slides = container.querySelectorAll('.roadtrip-slide');
    slides.forEach((slide, index) => slide.setAttribute('aria-label', (index + 1) + ' / ' + slides.length));
    state.done.style.strokeDasharray = state.total + ' ' + state.total;

    // Halte gleichmässig über die Breite verteilen und auf die Strasse setzen
    items.forEach((item, index) => {
      const x = items.length === 1 ? FIRST_X : FIRST_X + index * (LAST_X - FIRST_X) / (items.length - 1);
      const length = this.lengthAtX(state, x);
      const point = road.getPointAtLength(length);

      state.stopLength[index] = length;
      item.style.left = (point.x / VIEWBOX.w * 100) + '%';
      item.style.top = ((point.y - VIEWBOX.y) / VIEWBOX.h * 100) + '%';
    });

    stops.forEach((stop, index) => {
      stop.addEventListener('click', () => this.goTo(container, index));

      stop.addEventListener('keydown', (event) => {
        let next = null;

        if (event.key === 'ArrowRight') next = Math.min(stops.length - 1, index + 1);
        if (event.key === 'ArrowLeft') next = Math.max(0, index - 1);

        if (next !== null) {
          event.preventDefault();
          stops[next].focus();
          this.goTo(container, next);
        }
      });
    });

    this.placeCar(state, 0);
    this.attachSwiper(container, 0);

    // das Auto fährt los, sobald die Strasse sichtbar ist
    const map = container.querySelector('.roadtrip-map');

    if ('IntersectionObserver' in window && map) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;

        observer.disconnect();
        state.started = true;
        this.drive(state, this.carTarget(state, state.active));
      }, { threshold: 0.45 });

      observer.observe(map);
    } else {
      state.started = true;
      this.placeCar(state, this.carTarget(state, state.active));
    }
  }

  // Swiper wird in index.js initialisiert; ggf. kurz warten
  attachSwiper(container, attempt) {
    const state = container.roadtrip;
    const slider = container.querySelector('.js-slider');

    if (!state || !slider) return;

    if (!slider.swiper) {
      if (attempt < 60) {
        requestAnimationFrame(() => this.attachSwiper(container, attempt + 1));
      }
      return;
    }

    if (slider.swiper === state.swiper) return;

    state.swiper = slider.swiper;
    state.swiper.on('slideChange', (swiper) => {
      this.setActive(container, swiper.realIndex);
      this.updateSlideAccess(swiper);
    });
    this.setActive(container, state.swiper.realIndex);
    this.updateSlideAccess(state.swiper);
  }

  // Nur die sichtbare Slide ist per Tastatur und Screenreader erreichbar. Sonst scrollt der Browser beim Tabben
  // den Slider-Container selbst zu einer verdeckten Slide, und Swiper, Auto und Stopps laufen auseinander.
  updateSlideAccess(swiper) {
    swiper.slides.forEach((slide, i) => {
      slide.inert = i !== swiper.activeIndex;
    });
  }

  goTo(container, index) {
    const state = container.roadtrip;

    if (state.swiper) {
      state.swiper.slideTo(index);
    } else {
      this.setActive(container, index);
    }
  }

  setActive(container, index) {
    const state = container.roadtrip;

    state.active = Math.max(0, Math.min(state.stops.length - 1, index));

    state.stops.forEach((stop, i) => {
      const isActive = i === state.active;

      stop.classList.toggle('is-active', isActive);

      if (isActive) {
        stop.setAttribute('aria-current', 'step');
      } else {
        stop.removeAttribute('aria-current');
      }
    });

    if (state.started) {
      this.drive(state, this.carTarget(state, state.active));
    }
  }

  carTarget(state, index) {
    return Math.max(0, state.stopLength[index] - CAR_OFFSET);
  }

  // Länge auf dem Pfad zu einer x-Position (die Strasse verläuft stetig von links nach rechts)
  lengthAtX(state, x) {
    let low = 0;
    let high = state.total;

    for (let i = 0; i < 30; i++) {
      const mid = (low + high) / 2;

      if (state.road.getPointAtLength(mid).x < x) {
        low = mid;
      } else {
        high = mid;
      }
    }

    return (low + high) / 2;
  }

  placeCar(state, length) {
    const clamped = Math.max(0, Math.min(state.total, length));
    const point = state.road.getPointAtLength(clamped);
    const ahead = state.road.getPointAtLength(Math.min(state.total, clamped + 1));
    const angle = Math.atan2(ahead.y - point.y, ahead.x - point.x) * 180 / Math.PI;

    state.carAt = clamped;
    state.car.style.left = (point.x / VIEWBOX.w * 100) + '%';
    state.car.style.top = ((point.y - VIEWBOX.y) / VIEWBOX.h * 100) + '%';
    state.car.style.transform = 'translate(-50%, -50%) rotate(' + angle + 'deg)';
    state.done.style.strokeDashoffset = state.total - clamped;
  }

  // Fahrt wie im Prototyp: Dauer je nach Strecke, weich beschleunigen und bremsen
  drive(state, to) {
    if (state.anim) {
      cancelAnimationFrame(state.anim);
    }

    const from = state.carAt;
    const distance = Math.abs(to - from);

    if (this.reduceMotion || distance < 1) {
      this.placeCar(state, to);
      return;
    }

    const duration = Math.max(450, Math.min(1500, distance * 1.4));
    const start = performance.now();

    const step = (now) => {
      const k = Math.min(1, (now - start) / duration);
      const eased = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;

      this.placeCar(state, from + (to - from) * eased);

      if (k < 1) {
        state.anim = requestAnimationFrame(step);
      }
    };

    state.anim = requestAnimationFrame(step);
  }
}

export default new RoadTrip();
